import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

// Default assets by market. Symbols MUST be the platform's canonical
// identifiers (the ones SymbolsTable serves) — these go straight onto the
// user's watchlist, and anything else renders as a dead tile with no data.
const DEFAULT_ASSETS = {
  crypto: [
    { symbol: 'BTC/USD', name: 'Bitcoin' },
    { symbol: 'ETH/USD', name: 'Ethereum' },
    { symbol: 'SOL/USD', name: 'Solana' },
    { symbol: 'XRP/USD', name: 'Ripple' },
  ],
  stocks: [
    { symbol: 'AAPL', name: 'Apple' },
    { symbol: 'NVDA', name: 'NVIDIA' },
    { symbol: 'TSLA', name: 'Tesla' },
    { symbol: 'MSFT', name: 'Microsoft' },
    { symbol: 'SPY', name: 'S&P 500 ETF' },
    { symbol: 'QQQ', name: 'Nasdaq 100 ETF' },
  ],
  forex: [
    { symbol: 'EUR/USD', name: 'EUR/USD' },
    { symbol: 'GBP/USD', name: 'GBP/USD' },
    { symbol: 'JPY/USD', name: 'Japanese Yen' },
  ],
  commodities: [
    { symbol: 'XAU/USD', name: 'Gold' },
    { symbol: 'XAG/USD', name: 'Silver' },
    { symbol: 'WTI/USD', name: 'Crude Oil (WTI)' },
  ],
};

const AssetPickerStep = ({ selectedMarkets, selectedAssets, onSelectAssets }) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Get available assets based on selected markets
  const availableAssets = selectedMarkets.flatMap((market) =>
    (DEFAULT_ASSETS[market] || []).map((asset) => ({
      ...asset,
      market,
    }))
  );

  // Filter by search term
  const filteredAssets = searchTerm
    ? availableAssets.filter(
        (asset) =>
          asset.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
          asset.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : availableAssets;

  const isSelected = (symbol) =>
    selectedAssets.some((a) => a.symbol === symbol);

  const toggleAsset = (asset) => {
    if (isSelected(asset.symbol)) {
      onSelectAssets(selectedAssets.filter((a) => a.symbol !== asset.symbol));
    } else {
      onSelectAssets([...selectedAssets, asset]);
    }
  };

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          fontFamily: '"Space Grotesk", sans-serif',
          fontWeight: 600,
          mb: 1,
        }}
      >
        Pick your assets
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'var(--dwlf-ink-60)',
          mb: 3,
          fontFamily: '"IBM Plex Mono", monospace',
        }}
      >
        We've pre-selected some popular assets. Add or remove as you like.
      </Typography>

      {/* Search */}
      <TextField
        fullWidth
        placeholder="Search for an asset..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'var(--dwlf-ink-50)' }} />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 3,
          '& .MuiOutlinedInput-root': {
            bgcolor: 'var(--dwlf-elevated)',
            borderRadius: 0,
            fontFamily: '"Space Mono", monospace',
            '& fieldset': {
              borderColor: 'var(--dwlf-ink-10)',
              borderRadius: 0,
            },
            '&:hover fieldset': {
              borderColor: 'var(--dwlf-ink-20)',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'var(--dwlf-accent)',
            },
          },
          '& .MuiInputBase-input': {
            color: 'var(--dwlf-text)',
          },
        }}
      />

      {/* Selected count */}
      <Box sx={{ mb: 2 }}>
        <Chip
          label={`${selectedAssets.length} asset${selectedAssets.length !== 1 ? 's' : ''} selected`}
          sx={{
            bgcolor: 'var(--dwlf-accent-10)',
            color: 'var(--dwlf-accent)',
            border: '1px solid var(--dwlf-accent-30)',
            borderRadius: 0,
            fontFamily: '"Space Mono", monospace',
            fontSize: '0.75rem',
          }}
        />
      </Box>

      {/* Asset grid */}
      <Grid container spacing={1.5}>
        {filteredAssets.map((asset) => {
          const selected = isSelected(asset.symbol);

          return (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={asset.symbol}>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: selected ? 'var(--dwlf-accent-10)' : 'var(--dwlf-elevated)',
                  border: selected
                    ? '1px solid var(--dwlf-accent)'
                    : '1px solid var(--dwlf-ink-08)',
                  borderRadius: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: selected
                      ? 'var(--dwlf-accent-15)'
                      : 'var(--dwlf-ink-05)',
                  },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: '"Space Mono", monospace',
                      fontWeight: 600,
                      color: selected ? 'var(--dwlf-accent)' : 'var(--dwlf-text)',
                      fontSize: '0.9rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {asset.symbol}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'var(--dwlf-ink-50)',
                      fontFamily: '"IBM Plex Mono", monospace',
                      fontSize: '0.7rem',
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {asset.name}
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => toggleAsset(asset)}
                  size="small"
                  sx={{
                    ml: 1,
                    color: selected ? 'var(--dwlf-accent)' : 'var(--dwlf-ink-50)',
                    bgcolor: selected
                      ? 'var(--dwlf-accent-10)'
                      : 'var(--dwlf-ink-05)',
                    borderRadius: 0,
                    '&:hover': {
                      bgcolor: selected
                        ? 'var(--dwlf-accent-20)'
                        : 'var(--dwlf-ink-10)',
                    },
                  }}
                >
                  {selected ? <RemoveIcon /> : <AddIcon />}
                </IconButton>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {filteredAssets.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 6,
            color: 'var(--dwlf-ink-50)',
          }}
        >
          <Typography variant="body2" sx={{ fontFamily: '"IBM Plex Mono", monospace' }}>
            No assets found matching "{searchTerm}"
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AssetPickerStep;
